import type { ApolloError } from "@apollo/client"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Checkbox from "@/v2-ui/checkbox"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Manage from "@/v2-backoffice-client/components/manage"
import LicenseSeatPersonLabel from "@/v2-backoffice-client/license/seats/LicenseSeatPersonLabel"
import {
  type LicenseSeatLimitDraft,
  type LicenseSeatPerson,
  isSeatLimitCountValid,
  seatPersonNameOf,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"

export type LicenseSeatLimitFieldsProps = {
  /** The customer's active people, the ones a seat can be given to. */
  people: readonly LicenseSeatPerson[]
  isLoading: boolean
  error?: ApolloError
  customerName: string
  draft: LicenseSeatLimitDraft
  onDraftChange(next: LicenseSeatLimitDraft): void
  /**
   * Whether the seat count is the operator's own number (an operator grant:
   * it follows the people picked until typed) or the Fenerum subscription's
   * (a billing-derived license: shown as the placeholder, a typed number is
   * an override). Billing's own number, when it has one.
   */
  seatsFollowPicked: boolean
  billingSeats?: number | null
  /** Prefix for the input ids, so two instances on one page never collide. */
  idPrefix: string
}

// The two things a seat limit is made of, for the Seats dialog and the Grant
// dialog alike: who has the license, and how many seats the customer pays
// for. The count follows the ticks (one seat per person) so the common deal
// needs no typing; typing a different number records the deal as sold, and
// the row then shows named people against paid seats ("4 / 3" = over, for
// the account owner to invoice). A count never locks anyone out: the names
// decide who has the license.
function LicenseSeatLimitFields(props: LicenseSeatLimitFieldsProps) {
  const {
    people,
    isLoading,
    error,
    customerName,
    draft,
    onDraftChange,
    seatsFollowPicked,
    billingSeats,
    idPrefix,
  } = props

  const picked = draft.userIds.length
  const isFollowing = draft.seats === null
  const seatsValue =
    draft.seats ?? (seatsFollowPicked && picked > 0 ? String(picked) : "")
  const typedCount = draft.seats ? Number(draft.seats) : null
  const isCountValid = isSeatLimitCountValid(draft)

  function setUserIds(userIds: number[]) {
    onDraftChange({ ...draft, userIds })
  }

  const countNote = (() => {
    if (!seatsFollowPicked)
      return billingSeats != null
        ? `From the Fenerum subscription: ${billingSeats}. Type a number only to override it for ${customerName}.`
        : `The Fenerum subscription is not sold per seat. Type a number only if the deal says otherwise.`
    if (!isCountValid) return "A whole number of at least 1, or leave it empty."
    if (isFollowing)
      return "One seat per person picked. Change it if the customer pays for a different number: more people than seats shows as over on the license row."
    if (draft.seats === "")
      return `No seat count: the people are not held against a number. ${picked} picked.`
    return typedCount === picked
      ? `${picked} picked.`
      : `${picked} picked, ${typedCount} paid for: the row will show ${picked} / ${typedCount}.`
  })()

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="min-h-0 flex-1">
        <p className="mb-1 text-sm font-medium">
          Who has it{" "}
          <span className="font-normal text-muted-foreground">
            ({picked} of {people.length} picked)
          </span>
        </p>
        <ErrorMessage className="my-2" error={error} />
        {isLoading ? (
          <Manage.Skeleton count={5} aria-busy="true" />
        ) : people.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active users at {customerName} yet, so there is nobody to pick.
          </p>
        ) : (
          <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border px-3">
            {people.map((person) => {
              const isPicked = draft.userIds.includes(person.UserId)
              const inputId = `${idPrefix}-person-${person.UserId}`
              return (
                <li
                  key={person.UserId}
                  className="flex items-center justify-between gap-4 py-2"
                >
                  <label
                    htmlFor={inputId}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <LicenseSeatPersonLabel person={person} />
                  </label>
                  <Checkbox
                    id={inputId}
                    aria-label={`${seatPersonNameOf(person)} has the license`}
                    checked={isPicked}
                    onCheckedChange={(checked) => {
                      setUserIds(
                        checked === true
                          ? [...draft.userIds, person.UserId]
                          : draft.userIds.filter((id) => id !== person.UserId),
                      )
                    }}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="space-y-1 rounded-md border border-border px-3 py-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={`${idPrefix}-seats`}>Seats paid for</Label>
          {seatsFollowPicked && !isFollowing && picked > 0 && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              data-feature-code="UI_BackofficeLicense_Seats_CountFollowPicked"
              onClick={() => {
                onDraftChange({ ...draft, seats: null })
              }}
            >
              Use {picked}
            </button>
          )}
        </div>
        <Input
          id={`${idPrefix}-seats`}
          type="number"
          min={1}
          placeholder={
            seatsFollowPicked
              ? "None"
              : billingSeats != null
                ? String(billingSeats)
                : "Not counted"
          }
          aria-invalid={!isCountValid}
          value={seatsValue}
          onChange={(e) => {
            onDraftChange({ ...draft, seats: e.target.value })
          }}
        />
        <p className="text-xs text-muted-foreground">{countNote}</p>
      </div>
    </div>
  )
}

export default LicenseSeatLimitFields
